import { mapRecordFromServer, mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetOneResponse, IRequestPayload } from 'core/types';
import { getQueryStringToSnakeCase } from 'core/utils';
import {
  IAddElementTemplateToJobPayload,
  IComponentTemplate,
  IComponentTemplateRequestPayload,
  IElementTemplate,
  IUpsertComponentTemplateAssociationPayload,
  IUpsertComponentTemplatePayload,
  IUpsertElementTemplatePayload,
  IValidateComponentTemplatePart,
  IValidateComponentTemplatePartPayload,
} from 'elements/types';

export const getElementTemplates = async (
  requestPayload?: IRequestPayload,
  signal?: AbortSignal
) => {
  const { data } = await partfinitiClient.get(
    `/element/template/templates?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<IElementTemplate>(data.data, data.recordsFiltered);
};

export const getElementTemplate = async (
  requestPayload: IRequestPayload,
  signal?: AbortSignal
): Promise<GetOneResponse<IElementTemplate>> => {
  const { data } = await partfinitiClient.get(
    `/element/template/templates?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordFromServer<IElementTemplate>(data.data?.[0]);
};

export const upsertElementTemplate = async (data: IUpsertElementTemplatePayload) =>
  partfinitiClient.postForm('/element/template/save', data);

export const addElementTemplateToJob = async (data: IAddElementTemplateToJobPayload) =>
  partfinitiClient.postForm('/element/template/add-to-bom', data);

export const removeElementTemplates = async (data: { ids: number[] }) =>
  partfinitiClient.postForm('/element/template/remove', data);

export const getComponentTemplates = async (
  requestPayload?: IComponentTemplateRequestPayload,
  signal?: AbortSignal
) => {
  const { data } = await partfinitiClient.get(
    `/component/template/templates?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<IComponentTemplate>(data.data, data.recordsFiltered);
};

export const upsertComponentTemplate = async ({
  componentTemplate,
  viewBy,
}: IUpsertComponentTemplatePayload) => {
  const queryString = viewBy?.elementTemplateId
    ? `?viewBy[element_template_id]=${viewBy.elementTemplateId}`
    : '';

  return partfinitiClient.postForm(`/component/template/save${queryString}`, { componentTemplate });
};

export const removeComponentTemplateAssociations = async (data: { ids: number[] }) =>
  partfinitiClient.postForm('/element/component-template/remove', data);

export const upsertComponentTemplateAssociation = async (
  data: IUpsertComponentTemplateAssociationPayload
) => partfinitiClient.postForm('/element/component-template/save', data);

export const validateComponentTemplatePart = async (
  payload: IValidateComponentTemplatePartPayload,
  signal?: AbortSignal
): Promise<GetOneResponse<IValidateComponentTemplatePart>> => {
  const { data } = await partfinitiClient.get(
    `/component/template/validate-part?${getQueryStringToSnakeCase(payload, {
      removeNullValues: true,
    })}`,
    signal
  );

  return mapRecordFromServer<IValidateComponentTemplatePart>(data, data.success);
};
